/**
 * The HTTP client for the ported REST API.
 *
 * Everything the legacy transport did by hand — and got wrong — is centralised
 * here exactly once:
 *
 *  - **Authentication.** A Bearer access token, attached by an interceptor and
 *    refreshed transparently. The old client sent the caller's `accountnumber`
 *    and `unique_ID` in the body of every request (853 and 368 occurrences
 *    respectively) and the server believed them.
 *  - **Errors.** HTTP status codes, surfaced as a single `ApiError` type. The
 *    old backends answered HTTP 200 with `{status: "Error"}` and ~73 call
 *    sites sniffed for the substring "success"; a failed write was routinely
 *    reported to the user as a success.
 *  - **Envelopes.** None. The old responses were sometimes wrapped in an extra
 *    single-key object, and six independent copies of the same unwrapping
 *    heuristic existed across the codebase.
 */

import axios from 'axios';
import oidc from '@/auth/oidcClient';
import { nativeAdapter } from './legacyAdapterRef';
import { errorBodyOf } from './responseBody';

export const BASE_URL = (process.env.VUE_APP_API_BASE_URL || 'http://localhost:8200').replace(/\/$/, '');

/**
 * A failed API call, with the machine-readable code the server sent so callers
 * can branch on `err.code` instead of matching message strings.
 */
export class ApiError extends Error {
  constructor({ status, code, message, details, requestId }) {
    super(message || 'The request could not be completed.');
    this.name = 'ApiError';
    this.status = status;
    this.code = code || 'error';
    this.details = details || null;
    this.requestId = requestId || null;
  }

  /** Field-level validation messages, keyed by field name. */
  get fieldErrors() {
    return this.code === 'validation_failed' ? (this.details || {}) : {};
  }

  get isAuthError() {
    return this.status === 401;
  }

  get isForbidden() {
    return this.status === 403;
  }

  get isNotFound() {
    return this.status === 404;
  }

  get isConflict() {
    return this.status === 409;
  }
}

/**
 * The axios instance, or axios itself when the module has been auto-mocked.
 *
 * Unit specs written against the legacy transport say `jest.mock('axios')`
 * and assert on `axios.post`. An automock's `create()` returns `undefined`,
 * and this module is imported — through the session store, the privileges
 * store, the router — by almost every component, so the interceptor lines
 * below threw at import and whole spec files failed to load before a single
 * assertion ran. Falling back to the (mocked) module keeps importing this file
 * side-effect free under a mock, and any request a spec does make lands on the
 * mock it is already asserting against. A real axios always has `create`.
 */
function createHttp(config) {
  const instance = typeof axios.create === 'function' ? axios.create(config) : undefined;

  return instance || axios;
}

const http = createHttp({
  baseURL: `${BASE_URL}/api/v1`,
  timeout: 30_000,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
  // No cookies. The API is stateless and cross-origin; sending credentials
  // would be both useless and a CSRF surface.
  withCredentials: false,
  // Pinned to the real network adapter so these requests can never re-enter
  // the legacy bridge, which replaces the global default.
  adapter: nativeAdapter,
});

const interceptors = http.interceptors || {
  request: { use: () => undefined },
  response: { use: () => undefined },
};

interceptors.request.use(async (config) => {
  const token = await oidc.getAccessToken();

  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

let redirectingToLogin = false;

/**
 * Where to send the user back to after signing in again.
 *
 * The router runs on hash history, so `window.location.pathname` is always `/`
 * and using it threw away whatever page the user was on — they signed back in
 * and landed on the dashboard instead of the chart, note or order they were
 * working on. The route lives in the fragment; `oidcGuard` passes a router path
 * (`to.fullPath`) for the same parameter, so match that spelling.
 */
function currentRoutePath() {
  const hash = window.location.hash || '';
  return hash.startsWith('#') ? hash.slice(1) || '/' : (window.location.pathname || '/');
}

interceptors.response.use(
  (response) => response,
  async (error) => {
    const { response, config } = error;

    if (!response) {
      throw new ApiError({
        status: 0,
        code: 'network_error',
        message: 'Could not reach the server. Check your connection and try again.',
      });
    }

    // A download's refusal arrives as a Blob (BD-08): read it before anything
    // below decides on it, so a 401 `mfa_required` on a document, a CDA or a
    // message attachment is recognised as one, and every other refusal still
    // carries its code (responseBody.js).
    const body = await errorBodyOf(response.data);

    // One retry after a token refresh: the token may simply have aged out
    // between the interceptor attaching it and the server reading it.
    //
    // The re-login branch is deliberately *outside* the retry guard. It used to
    // be inside, which made it unreachable in the case that matters: the retry
    // stamps `__retriedAfterRefresh` on the same `config` object it re-sends, so
    // when the retried request also came back 401 the whole block was skipped
    // and the error fell through to a plain ApiError. The bridge then turned
    // that into an HTTP 200 carrying `{status:'Error', results: []}`, so an
    // expired session showed up as lists that quietly stopped updating while
    // the tab kept hammering the API — and the user went on believing they were
    // looking at current data.
    if (response.status === 401) {
      // A second factor too old for this endpoint (pacmny-api
      // RequireSecondFactor; RFC 9470 `insufficient_user_authentication`).
      // Refreshing cannot help - the new token carries the same `auth_time` -
      // so there is no retry: the person signs in afresh, and `prompt=login`
      // makes the authorization server ask for the password and a new code
      // rather than reissue from its own session. Main sends the person back
      // to the code page for the same refusal (src/api/mfaRequired.js).
      const needsFreshFactor = (body.error || {}).code === 'mfa_required';

      if (!needsFreshFactor && !config.__retriedAfterRefresh) {
        config.__retriedAfterRefresh = true;
        const token = await oidc.getAccessToken();

        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
          return http.request(config);
        }
      }

      // Either there was nothing to refresh with, or the refreshed token was
      // refused too. The session is over either way.
      if (!redirectingToLogin) {
        redirectingToLogin = true;

        try {
          oidc.clearSession();
          await oidc.beginLogin(needsFreshFactor
            ? { returnTo: currentRoutePath(), prompt: 'login' }
            : { returnTo: currentRoutePath() });
        } catch (e) {
          // The provider is unreachable. Drop the latch so a later attempt can
          // try again rather than leaving the tab permanently unable to
          // re-authenticate.
          redirectingToLogin = false;
          throw e;
        }
      }
    }

    const payload = body.error || {};

    throw new ApiError({
      status: response.status,
      code: payload.code,
      message: payload.message,
      details: payload.details,
      requestId: response.headers?.['x-error-reference'],
    });
  },
);

/** Unwrap Laravel's `{data: …}` resource envelope, which is the only one left. */
function unwrap(response) {
  const body = response.data;
  return body && Object.prototype.hasOwnProperty.call(body, 'data') ? body.data : body;
}

/** A paginated list, with the page metadata kept alongside the rows. */
function unwrapPage(response) {
  const body = response.data || {};
  return {
    items: body.data || [],
    meta: body.meta || null,
    links: body.links || null,
  };
}

export const api = {
  async get(path, params = {}, config = {}) {
    return unwrap(await http.get(path, { params, ...config }));
  },

  async list(path, params = {}, config = {}) {
    return unwrapPage(await http.get(path, { params, ...config }));
  },

  async post(path, body = {}, config = {}) {
    return unwrap(await http.post(path, body, config));
  },

  async put(path, body = {}, config = {}) {
    return unwrap(await http.put(path, body, config));
  },

  async patch(path, body = {}, config = {}) {
    return unwrap(await http.patch(path, body, config));
  },

  async delete(path, config = {}) {
    const response = await http.delete(path, config);
    return response.status === 204 ? null : unwrap(response);
  },

  /** Multipart upload; the browser sets the boundary, so the header is removed. */
  async upload(path, formData, { onProgress, ...config } = {}) {
    return unwrap(await http.post(path, formData, {
      ...config,
      headers: { ...(config.headers || {}), 'Content-Type': undefined },
      onUploadProgress: onProgress
        ? (e) => onProgress(e.total ? Math.round((e.loaded * 100) / e.total) : 0)
        : undefined,
    }));
  },

  /** Binary download (PDFs, CCDA exports, generated reports). */
  async download(path, params = {}) {
    const response = await http.get(path, { params, responseType: 'blob' });
    return {
      blob: response.data,
      filename: parseFilename(response.headers?.['content-disposition']),
      contentType: response.headers?.['content-type'],
    };
  },

  raw: http,
};

function parseFilename(disposition) {
  if (!disposition) return null;
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  return match ? decodeURIComponent(match[1]) : null;
}

export default api;
